// Sequences reminder pop-ups so simultaneous reminders show one after another
// instead of overwriting each other. Reminders are the only notification type
// that can fire twice in the same instant, so only they go through this queue.

// Mirrors the validity guards the show path applies to the reminder type:
// an entry that would be rejected there must not occupy the queue.
export function isShowableReminder (payload) {
  return Boolean(payload?.title && payload?.message)
}

// Deduped against both the visible slot and the queue, keyed by the
// engagement id, so a re-broadcast of the same reminder shows only once.
export function isReminderQueuedOrVisible (store, payload) {
  const communicationId = payload?.communicationId ?? null

  if (!communicationId) {
    return false
  }

  if (store.state.notifications?.reminder?.communicationId === communicationId) {
    return true
  }

  return store.state.reminderNotificationQueue.some(item => item.communicationId === communicationId)
}

export function enqueueReminderNotification (store, payload) {
  if (!isShowableReminder(payload) || isReminderQueuedOrVisible(store, payload)) {
    return false
  }

  store.commit('ADD_TO_REMINDER_NOTIFICATION_QUEUE', payload)
  return true
}

// The visible check reads the store, not the DOM: the toast renders a tick
// after the show commit, so a DOM lookup misses a reminder shown in the same
// tick — exactly the simultaneous case this queue exists for.
export function hasVisibleReminder (store) {
  return Boolean(store.state.notifications?.reminder?.title)
}

// Shows at most one reminder per call; the toast's hidden hook drains the
// next. Invalid entries are skipped so a rejected payload cannot stall the
// queue. Sleep mode discards the queue outright — queued reminders must not
// surface once notifications are muted.
export function drainReminderNotificationQueue (store, show) {
  if (store.state.auth?.profile?.sleep_mode) {
    store.commit('CLEAR_REMINDER_NOTIFICATION_QUEUE')
    return
  }

  while (store.state.reminderNotificationQueue.length) {
    if (hasVisibleReminder(store)) {
      return
    }

    const next = store.state.reminderNotificationQueue[0]
    store.commit('SHIFT_REMINDER_NOTIFICATION_QUEUE')

    if (!isShowableReminder(next)) {
      continue
    }

    show(next)
    return
  }
}
